import React, { useContext, useEffect, useRef, useState } from "react";
import MetaTags from "react-meta-tags";
import { LanguageContext } from "./../language-context";
import { PageTitle } from "./helper/PageTitle";
import { Scroll } from "./helper/Scroll";
import * as variables from "./helper/Variables";
import styled from "styled-components";
import { pl } from "./../data/pl";
import { RiArrowUpSLine, RiArrowDownSLine } from "react-icons/ri";

// Layout
const Layout = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  background-color: ${variables.BACKGROUND_GRAY};
  overflow: hidden;

  @media (max-width: 1000px) {
    flex-direction: column;
    height: auto;
  }
`;

// Desktop TOC
const Sidebar = styled.nav`
  max-width: 25rem;
  background-color: ${variables.BACKGROUND_GRAY};
  padding: 7rem 1rem 0 2rem;
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100vh;
  overflow-y: auto;
  font-size: 1rem;

  @media (max-width: 1000px) {
    display: none;
  }
`;

const TOCList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TOCItem = styled.li`
  margin-bottom: 0.5rem;
  cursor: pointer;
  color: black;
  transition: all 0.2s ease;
  font-weight: ${(props) => (props.active ? "bold" : "normal")};

  &:hover {
    text-decoration: none;
    font-weight: bold;
  }
`;

// Wrapper
const Wrapper = styled.div`
  flex-grow: 1;
  height: 100vh;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  scroll-behavior: smooth;
`;

// Sekcja
const Section = styled.section`
  height: 100vh;
  scroll-snap-align: start;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${variables.BACKGROUND_GRAY};
  white-space: pre-wrap;
  padding: 2rem;

  @media (max-width: 1000px) {
    padding-top: 8rem;
    font-size: ${variables.SMALL_MOBILE_FONT};
    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3rem;
      pointer-events: none;
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0),
        ${variables.BACKGROUND_GRAY} 100%
      );
    }

    p {
      height: 100%;
      overflow-y: scroll;
      scrollbar-color: ${variables.DIMGRAY} ${variables.BACKGROUND_GRAY};
      scrollbar-width: thin;
    }
  }
`;

// Mobile TOC
const MobileTOCWrapper = styled.div`
  display: none;

  @media (max-width: 1000px) {
    display: block;
    position: fixed;
    top: 5rem;
    left: 0;
    right: 0;
    z-index: 1;
    background-color: ${(props) =>
      props.expanded ? variables.BACKGROUND_GRAY : "transparent"};
    transition: background-color 0.3s ease;
  }
`;

const MobileTOCHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 2rem;
  font-weight: bold;
  cursor: pointer;
  background-color: transparent;
`;

const MobileTOCList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 2rem 1rem;
`;

const MobileTOCItem = styled.li`
  padding: 0.5rem 0;
  cursor: pointer;
  font-weight: ${(props) => (props.active ? "bold" : "normal")};

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 1000px) {
    padding: 0.15rem 0;
    font-size: ${variables.MOBILE_FONT};
  }
`;

export const Poetry = () => {
  const poetryBookId = window.location.href.slice(-4);
  const usedLanguage = useContext(LanguageContext);

  // Early return fix — hooks always run
  const notFound = pl.poetry[poetryBookId] === undefined;
  Scroll();

  const { poetryBookTitle, poems } = pl.poetry[poetryBookId] || {};
  const sections = Object.entries(poems || {}).map(([key, value]) => ({
    id: key,
    label: value,
  }));

  const [activeSection, setActiveSection] = useState(sections[0]?.id || "");
  const sectionRefs = useRef({});
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });

      // Ustawiamy ręcznie, bo IntersectionObserver może się nie odświeżyć natychmiast
      setTimeout(() => {
        setActiveSection(id);
      }, 300);

      setMobileTocOpen(false);
    }
  };

  // IntersectionObserver
  useEffect(() => {
    if (notFound) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        root: document.querySelector("#scroll-wrapper"),
        threshold: 0.5,
      }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [notFound]);

  if (notFound) {
    window.location.replace(`http://www.michalkrol.com/#/404`);
    window.location.reload();
    return null;
  }

  return (
    <main>
      <MetaTags>
        <title>{poetryBookTitle} | Structuralist</title>
        <meta name="description" content={poetryBookTitle} />
        <meta
          name="keywords"
          content={`"Structuralist", "Michał Król", ${poetryBookTitle}`}
        />
      </MetaTags>

      <PageTitle
        text={poetryBookTitle}
        backgroundColor={variables.BACKGROUND_GRAY}
      />

      <Layout>
        {/* Desktop TOC */}
        <Sidebar>
          <TOCList>
            {sections.map(({ id }) => (
              <TOCItem
                key={id}
                onClick={() => scrollToSection(id)}
                active={activeSection === id}
              >
                {id}
              </TOCItem>
            ))}
          </TOCList>
        </Sidebar>

        {/* Mobile TOC */}
        <MobileTOCWrapper expanded={mobileTocOpen}>
          <MobileTOCHeader onClick={() => setMobileTocOpen(!mobileTocOpen)}>
            {activeSection}
            {mobileTocOpen ? (
              <RiArrowUpSLine size={24} />
            ) : (
              <RiArrowDownSLine size={24} />
            )}
          </MobileTOCHeader>

          {mobileTocOpen && (
            <MobileTOCList>
              {sections.map(({ id }) => (
                <MobileTOCItem
                  key={id}
                  active={activeSection === id}
                  onClick={() => scrollToSection(id)}
                >
                  {id}
                </MobileTOCItem>
              ))}
            </MobileTOCList>
          )}
        </MobileTOCWrapper>

        {/* Content */}
        <Wrapper id="scroll-wrapper">
          {sections.map(({ id, label }) => (
            <Section
              key={id}
              id={id}
              ref={(el) => (sectionRefs.current[id] = el)}
            >
              <p>{label}</p>
            </Section>
          ))}
        </Wrapper>
      </Layout>
    </main>
  );
};
